import { isLegacyChainflipAsset, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { abbreviate } from '@chainflip/utils/string';
import { Link } from '@/shared/components/atoms/Link';
import Pill, { type PillColor } from '@/shared/components/flip-ui-kit/Pill';
import BoostPoolLogo from '@/shared/components/logo/BoostPoolLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import ViewEventButton from '@/shared/components/ViewEventButton';
import { type BoostPoolBalanceChangeType } from '@/shared/graphql/generated/lp/graphql';
import {
  getAccountBoostLiquidityBalanceChangesQuery,
  getBoostPoolLiquidityBalanceChangeQuery,
} from '@/shared/graphql/lp/boost';
import useBoostPoolId from '@/shared/hooks/useBoostPoolId';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import WarningTriangle from '@/shared/icons/flip-ui-kit/small/WarningTriangle';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue, pipsToPercentString } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type PoolActivityEventType = Exclude<BoostPoolBalanceChangeType, 'COLLECTED_FEES'>;

const pillColor = {
  DELAYED_WITHDRAWAL: 'yellow',
  WITHDRAWAL: 'yellow',
  FUNDS_ADDED: 'purple',
  FUNDS_LOST: 'red',
} as const satisfies Record<PoolActivityEventType, PillColor>;

const pillText = {
  WITHDRAWAL: 'Remove liquidity',
  DELAYED_WITHDRAWAL: 'Remove liquidity',
  FUNDS_ADDED: 'Add liquidity',
  FUNDS_LOST: 'Funds lost',
} as const satisfies Record<PoolActivityEventType, string>;

const pillIcon: Partial<Record<PoolActivityEventType, React.FC>> = {
  FUNDS_LOST: WarningTriangle,
};

const EventPill = ({ type }: { type: PoolActivityEventType }) => (
  <Pill color={pillColor[type]} text={pillText[type]} Icon={pillIcon[type]} />
);

const PoolActivityRow = ({
  type,
  lpIdSs58,
  amount,
  amountUsd,
  event,
  includePool,
  blockExplorerUrl,
}: {
  type: PoolActivityEventType;
  lpIdSs58: string | undefined;
  amount: string;
  amountUsd: string | undefined | null;
  event: { timestamp: string; block: number; indexInBlock: number };
  includePool?: boolean;
  blockExplorerUrl?: string;
}) => {
  // TODO(boost): hard coded values used here, should be updated if we introduce new boost pools
  const asset = 'Btc';
  const feeTierPips = 5;

  if (isLegacyChainflipAsset(asset)) return null;

  const lpPath = `/lps/${lpIdSs58}`;
  const lpUrl = blockExplorerUrl ? new URL(lpPath, blockExplorerUrl).toString() : lpPath;

  return (
    <tr className="text-cf-light-3">
      <td className="whitespace-nowrap">
        <EventPill type={type} />
      </td>
      {lpIdSs58 !== undefined && (
        <td>
          <div className="flex h-full items-center gap-x-2 whitespace-nowrap">
            <Link href={lpUrl} target={blockExplorerUrl ? '_blank' : undefined} noPropagate>
              <span className="underline">{abbreviate(lpIdSs58, 8)}</span>
            </Link>
            <CopyButton textToCopy={lpIdSs58 as string} />
          </div>
        </td>
      )}
      {includePool && (
        <td>
          <div className="flex h-full items-center gap-x-2">
            <BoostPoolLogo />
            <Pill text={pipsToPercentString(feeTierPips)} color="neutral" />
          </div>
        </td>
      )}
      <td className="whitespace-nowrap">
        <div className="flex flex-col gap-y-px text-14">
          <span className="flex items-center gap-x-2 text-cf-light-3">
            {!includePool && <TokenWithChainByAsset asset={asset} />}
            {TokenAmount.fromAsset(amount, asset).toFormatted()} {chainflipAssetMap[asset].symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(amountUsd ?? '0')}</span>
        </div>
      </td>
      <td className="whitespace-nowrap">
        <ViewEventButton event={{ blockId: event.block, indexInBlock: event.indexInBlock }} />
      </td>
      <td className="whitespace-nowrap text-right text-12 text-cf-light-2">
        <AgeWithTooltip timestamp={event.timestamp} />
      </td>
    </tr>
  );
};

export const PoolActivityTable = ({
  asset,
  feeBps,
  accountId,
  blockExplorerUrl,
}: {
  asset?: ChainflipAsset;
  feeBps?: number;
  accountId?: string;
  blockExplorerUrl?: string;
}) => {
  const isAccountView = accountId !== undefined;
  const { boostPoolId } = useBoostPoolId({ asset, feeBps });
  const allPools = asset === undefined && feeBps === undefined;
  const {
    data: poolBalanceChangeData,
    paginationControls: poolPaginationControls,
    isLoading: poolBalanceChangeLoading,
  } = useRemotePagination(getBoostPoolLiquidityBalanceChangeQuery, {
    context: { clientName: 'lpProcessor' },
    variables: { boostPoolId },
    enabled: !isAccountView,
    pageInfo: (data) => data.balanceChanges?.pageInfo,
  });
  const {
    data: accountBalanceChangeData,
    paginationControls: accountPaginationControls,
    isLoading: accountPoolBalanceChangeLoading,
  } = useRemotePagination(getAccountBoostLiquidityBalanceChangesQuery, {
    context: { clientName: 'lpProcessor' },
    variables: { boostPoolId, accountId: accountId as string },
    enabled: isAccountView,
    pageInfo: (data) => data.account?.balanceChanges.pageInfo,
  });

  const balanceChanges =
    (!isAccountView ? poolBalanceChangeData : accountBalanceChangeData?.account)?.balanceChanges
      ?.edges ?? [];

  return (
    <TableV2
      isLoading={poolBalanceChangeLoading || accountPoolBalanceChangeLoading}
      columns={[
        'Type',
        ...(isAccountView ? [] : ['Account ID']),
        ...(allPools ? ['Boost Pool', 'Amount'] : ['Amount']),
        '',
        { name: 'Created', alignment: 'right' },
      ]}
      rows={
        balanceChanges?.map(({ node }) => (
          <PoolActivityRow
            key={`${node.event.block}-${node.event.indexInBlock}`}
            type={node.type as PoolActivityEventType}
            lpIdSs58={isAccountView ? undefined : node.lp.idSs58}
            amount={node.amount}
            amountUsd={node.amountUsd}
            event={node.event}
            includePool={allPools}
            blockExplorerUrl={blockExplorerUrl}
          />
        )) ?? []
      }
      paginationControls={
        accountId === undefined ? poolPaginationControls : accountPaginationControls
      }
    />
  );
};
